import { useEffect, useRef } from "react";
import { Chart } from "chart.js/auto";
import {
  drawNonOverlappingLabel,
  getInitialJitter,
} from "../../../utils/drawNonOverlappingLabel";

const convertToFeetInches = (inches) => {
  const feet = Math.floor(inches / 12);
  const remainingInches = inches % 12;
  return `${feet}'${remainingInches.toFixed(3)}"`;
};

const getYAxisLimits = (results) => {
  const valid = results.filter((r) => typeof r === "number");
  const max = Math.max(...valid);
  const min = Math.min(...valid);
  const range = max - min;
  const margin = range < 5 ? 2 : range * 0.05;
  return {
    max: max + margin,
    min: valid.includes(0) ? 0 : Math.max(min - margin, 0),
  };
};

const GraphMeasurement = ({ measurementData, title }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (measurementData.length > 0) {
      const sortedData = [...measurementData].sort(
        (a, b) => new Date(a.combineDate) - new Date(b.combineDate),
      );

      const ctx = chartRef.current.getContext("2d");
      if (chartRef.current.chart) chartRef.current.chart.destroy();

      const results = sortedData.map((entry) => entry.result);
      const unit = sortedData[0]?.unit?.toLowerCase();
      const improvementIsHigher = unit !== "seconds";
      const offset = (Math.max(...results) - Math.min(...results)) * 0.02;

      const { min, max } = getYAxisLimits(results);

      const pointLabelPlugin = {
        id: "pointLabelPlugin",
        afterDatasetsDraw(chart) {
          const { ctx } = chart;
          const dataset = chart.data.datasets[0];
          const meta = chart.getDatasetMeta(0);

          const placed = [];

          ctx.save();

          for (let i = 0; i < meta.data.length; i++) {
            const point = meta.data[i];
            const value = dataset.data[i];
            if (typeof value !== "number") continue;

            const x = point.x;
            const jitter = getInitialJitter(meta, i);
            const baseY = point.y - 12 + jitter;

            drawNonOverlappingLabel(ctx, value.toFixed(3), x, baseY, placed, {
              minY: 8,
              stepY: 14,
            });
          }

          ctx.restore();
        },
      };

      chartRef.current.chart = new Chart(ctx, {
        type: "line",
        data: {
          labels: sortedData.map((entry) =>
            new Date(entry.combineDate).toLocaleDateString(),
          ),
          datasets: [
            {
              label: "Measurement",
              data: results,
              borderColor: "blue",
              backgroundColor: "rgba(0, 250, 0, 0.2)",
              pointRadius: 0,
              tension: 0.4,
              cubicInterpolationMode: "monotone",
              segment: {
                borderColor: (ctx) => {
                  const i = ctx.p0DataIndex;
                  const prev = results[i];
                  const curr = results[i + 1];
                  if (typeof curr !== "number") return "black";
                  const improved = improvementIsHigher
                    ? curr > prev
                    : curr < prev;
                  if (curr === prev) return "black";
                  return improved ? "green" : "red";
                },
              },
            },
            {
              label: "Points Highlight",
              data: results.map((val) => val + offset),
              borderColor: "transparent",
              backgroundColor: "rgba(0, 255, 0, 0.7)",
              pointStyle: sortedData.map((entry, idx) => {
                if (idx === 0) return "circle";
                const prev = results[idx - 1];
                const curr = results[idx];
                if (curr === prev) return "circle";
                return improvementIsHigher
                  ? curr > prev
                    ? "triangle"
                    : "rectRot"
                  : curr < prev
                    ? "triangle"
                    : "rectRot";
              }),
              pointRadius: 6,
              pointHoverRadius: 10,
              pointBorderColor: "white",
              pointBorderWidth: 2,
              showLine: false,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            mode: "index",
            intersect: false,
          },
          animation: {
            duration: 500,
            easing: "easeOutElastic",
          },
          layout: {
            padding: {
              top: 20,
            },
          },
          plugins: {
            title: {
              display: false,
            },
            legend: {
              display: false,
            },
            tooltip: {
              position: "average",
              yAlign: "bottom",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              displayColors: false,
              callbacks: {
                label: function (tooltipItem) {
                  if (tooltipItem.datasetIndex !== 0) return "";

                  const entry = sortedData[tooltipItem.dataIndex];
                  const value = results[tooltipItem.dataIndex];
                  const prev = results[tooltipItem.dataIndex - 1];
                  const delta = typeof prev === "number" ? value - prev : null;

                  const icon =
                    delta === 0
                      ? "⚫"
                      : improvementIsHigher
                        ? delta > 0
                          ? "🔺"
                          : "🔻"
                        : delta < 0
                          ? "🔺"
                          : "🔻";

                  const combine = entry.combineName || "";
                  let label = `${value} ${entry.unit}${delta !== null ? ` ${icon}` : ""}`;

                  const name =
                    typeof title === "string" ? title.toLowerCase() : "";
                  if (name.includes("height") || name.includes("wingspan")) {
                    label += ` (${convertToFeetInches(value)})`;
                  }

                  return [`Combine: ${combine}`, label];
                },
              },
            },
          },
          scales: {
            x: {
              offset: true,
              ticks: { padding: 10 },
              grid: { drawOnChartArea: false },
              title: {
                display: true,
                text: "Events",
              },
            },
            y: {
              type: "linear",
              position: "left",
              min,
              max,
              ticks: {
                stepSize: 1,
                callback: (value) => value.toFixed(0),
              },
              grid: { drawOnChartArea: false },
              title: {
                display: true,
                text: `${unit || "Units"}`,
                font: {
                  size: 14,
                  weight: "bold",
                },
              },
            },
          },
        },
        plugins: [pointLabelPlugin],
      });

      const firstResult = results[0];
      const lastResult = results[results.length - 1];
      const delta = lastResult - firstResult;

      const changeColor =
        delta > 0
          ? "text-green-600"
          : delta < 0
            ? "text-red-600"
            : "text-black";
      const changeText =
        delta !== 0
          ? `Overall change: ${delta.toFixed(3)} ${unit}`
          : "No change";

      const changeDisplay = document.createElement("div");
      changeDisplay.className = `mt-2 text-center font-semibold ${changeColor}`;
      changeDisplay.innerText = changeText;
      chartRef.current.parentNode.appendChild(changeDisplay);
    }
  }, [measurementData, title]);

  return (
    <div className="flex flex-col items-center mt-6">
      <h1 className="text-xl mb-4 text-center">{title}</h1>
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

export default GraphMeasurement;
